import React from "react";
import { observer } from "mobx-react";
import {
  Alert,
  Box,
  CircularProgress,
  Drawer,
  Grid2,
  IconButton,
  Paper,
  Skeleton,
  Tooltip,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import {
  getModernPageColors,
  modernDrawerBodySx,
  modernDrawerHeaderSx,
} from "../../../../../../styles/modernListingPageSx";
import { PodRevenueDashboardStore } from "../../../../stores/dashboards/PodRevenueDashboardStore";
import {
  formatPodCount,
  formatPodDateTime,
  formatPodMoney,
  formatPodShare,
} from "./podRevenueFormat";
import PodRevenueLinesTable from "./PodRevenueLinesTable";

const DRAWER_Z_OFFSET = 2000;

/** Product side drawer, same shell as RedemptionDrawer: header, stat cards, line listing. */
const PodRevenueDrawer: React.FC<{ store: PodRevenueDashboardStore }> =
  observer(({ store }) => {
    const theme = useTheme();
    const colors = getModernPageColors(theme);
    const customColors = theme.palette.customColors;
    const product = store.drawerProduct;
    const order = store.drawerOrder;
    const totalGross = store.summary?.data.kpis.gross_revenue || 0;

    const stats: Array<{ label: string; value: string; hero?: boolean }> = order
      ? [
          { label: "Units", value: formatPodCount(order.unit_count) },
          {
            label: "Selling excl.",
            value: formatPodMoney(order.selling_exclusive),
          },
          { label: "GST", value: formatPodMoney(order.gst_amount) },
          { label: "Handling", value: formatPodMoney(order.handling_amount) },
          { label: "Shipping", value: formatPodMoney(order.shipping_amount) },
          {
            label: "Order gross",
            value: formatPodMoney(order.gross_revenue),
            hero: true,
          },
        ]
      : product
        ? [
            { label: "Orders", value: formatPodCount(product.order_count) },
            { label: "Units", value: formatPodCount(product.unit_count) },
            {
              label: "Selling excl.",
              value: formatPodMoney(product.selling_exclusive),
            },
            { label: "GST", value: formatPodMoney(product.gst_amount) },
            {
              label: "Handling",
              value: formatPodMoney(product.handling_amount),
            },
            {
              label: "Shipping",
              value: formatPodMoney(product.shipping_amount),
            },
            {
              label: "Gross",
              value: formatPodMoney(product.gross_revenue),
              hero: true,
            },
          ]
        : [];

    return (
      <Drawer
        anchor="right"
        open={store.drawerOpen}
        onClose={store.closeDrawer}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 880, md: 960 },
            maxWidth: "100vw",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
        transitionDuration={{ enter: 280, exit: 200 }}
        ModalProps={{
          sx: { zIndex: (t) => t.zIndex.drawer + DRAWER_Z_OFFSET },
          slotProps: {
            backdrop: {
              sx: {
                backdropFilter: "blur(2px)",
                bgcolor: "rgba(15, 23, 42, 0.28)",
              },
            },
          },
        }}
      >
        <Box sx={modernDrawerHeaderSx(theme)}>
          <Box display="flex" gap={1.5} alignItems="flex-start" minWidth={0}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: customColors.lightBlue,
                color: customColors.darkBlue,
              }}
            >
              <InsightsOutlinedIcon fontSize="small" />
            </Box>
            <Box minWidth={0}>
              <CustomTypography
                variant="caption"
                sx={{
                  color: colors.subtitle,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  fontWeight: 600,
                }}
              >
                {order ? "POD order" : "POD product revenue"}
              </CustomTypography>
              <CustomTypography
                variant="heading5"
                sx={{ color: colors.title, wordBreak: "break-word" }}
              >
                {order
                  ? `Order #${order.order_id}`
                  : product?.product_name ||
                    (product ? `Product #${product.product_id}` : "")}
              </CustomTypography>
              {order ? (
                <CustomTypography
                  variant="body4"
                  sx={{ mt: 0.5, color: colors.subtitle }}
                >
                  {order.customer_name || "—"} · {order.store_name || "—"}
                  {order.order_date
                    ? ` · ${formatPodDateTime(order.order_date)}`
                    : ""}
                  {order.cart_id ? ` · cart ${order.cart_id}` : ""}
                </CustomTypography>
              ) : (
                <CustomTypography
                  variant="body4"
                  sx={{ mt: 0.5, color: colors.subtitle }}
                >
                  {product?.sku || "—"} · #{product?.product_id}
                  {product && totalGross > 0
                    ? ` · ${formatPodShare(product.gross_revenue, totalGross)} of total POD gross`
                    : ""}
                </CustomTypography>
              )}
            </Box>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5} flexShrink={0}>
            <Tooltip
              title="Refresh"
              slotProps={{
                popper: { sx: { zIndex: (t) => t.zIndex.drawer + 5000 } },
              }}
            >
              <span>
                <IconButton
                  onClick={() => void store.fetchDrawer()}
                  size="small"
                  aria-label="Refresh"
                  disabled={store.drawerLoading}
                >
                  {store.drawerLoading ? (
                    <CircularProgress size={18} />
                  ) : (
                    <RefreshIcon />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip
              title="Close (Esc)"
              slotProps={{
                popper: { sx: { zIndex: (t) => t.zIndex.drawer + 5000 } },
              }}
            >
              <IconButton
                onClick={store.closeDrawer}
                size="small"
                aria-label="Close"
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box
          sx={{
            ...modernDrawerBodySx(),
            flex: 1,
            minHeight: 0,
            overflow: "auto",
          }}
        >
          {store.drawerError ? (
            <Alert
              severity="error"
              action={
                <CustomTypography
                  variant="heading8"
                  fontWeight={600}
                  sx={{ cursor: "pointer", alignSelf: "center", pr: 1 }}
                  onClick={() => void store.fetchDrawer()}
                >
                  Retry
                </CustomTypography>
              }
            >
              {store.drawerError}
            </Alert>
          ) : null}
          <Grid2 container spacing={1.5}>
            {stats.map((stat) => (
              <Grid2 key={stat.label} size={{ xs: 6, sm: 4, md: 3 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    height: "100%",
                    borderRadius: 2.5,
                    bgcolor: stat.hero
                      ? customColors.lightBlue
                      : customColors.white,
                    border: `1px solid ${stat.hero ? customColors.darkBlue : customColors.softCardBorder}`,
                    borderLeft: `4px solid ${customColors.darkBlue}`,
                    transition: "transform .15s ease, box-shadow .15s ease",
                    "&:hover": {
                      transform: "translateY(-1px)",
                      boxShadow: "0 4px 14px rgba(15, 23, 42, 0.08)",
                    },
                  }}
                >
                  <CustomTypography
                    variant="body4"
                    sx={{
                      color: customColors.mutedSlateText,
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      textTransform: "uppercase",
                    }}
                  >
                    {stat.label}
                  </CustomTypography>
                  <CustomTypography
                    variant="heading5"
                    sx={{
                      mt: 0.5,
                      color: customColors.navyText,
                      fontWeight: 700,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {stat.value}
                  </CustomTypography>
                </Paper>
              </Grid2>
            ))}
          </Grid2>
          <Box
            display="flex"
            alignItems="baseline"
            justifyContent="space-between"
            gap={1}
            flexWrap="wrap"
          >
            <CustomTypography variant="heading7" sx={{ color: colors.title }}>
              {order ? "Products" : "Order lines"} ·{" "}
              {store.drawerLoading && store.drawerLines.rows.length === 0 ? (
                <Skeleton
                  variant="text"
                  width={32}
                  sx={{ display: "inline-block" }}
                />
              ) : (
                formatPodCount(store.drawerLines.total)
              )}
            </CustomTypography>
            <CustomTypography variant="body4" sx={{ color: colors.subtitle }}>
              {order
                ? "Line total = selling excl. + GST. Handling/shipping are order services (see totals above)."
                : "Handling/shipping are POD order services (excl. GST), split across the order's lines."}
            </CustomTypography>
          </Box>
          <PodRevenueLinesTable
            hideProduct={!order}
            hideOrder={!!order}
            hideOrderCharges={!!order}
            rows={store.drawerLines.rows}
            page={store.drawerLines.page}
            total={store.drawerLines.total}
            maxHeight={560}
            pages={store.drawerLines.pages}
            loading={store.drawerLoading}
            error={null}
            sortBy={store.drawerLines.sortBy}
            sortType={store.drawerLines.sortType}
            onSort={(s) => void store.setDrawerSort(s)}
            onPage={(p) => void store.setDrawerPage(p)}
          />
        </Box>
      </Drawer>
    );
  });

export default PodRevenueDrawer;
